import { useCallback } from "react";
import type { CustomView, GroupColors } from "../../../../datagrid/ColumnVisibilityModal";
import type { ColumnConfig, ColumnVisibility } from "../../../../datagrid-configs/types";
import type { GameRow } from "../../../../datagrid-configs/discoverGames";
import { useDiscoverGamesColumnLayout } from "./useDiscoverGamesColumnLayout";
import { useDiscoverGamesGrading } from "./useDiscoverGamesGrading";
import { useDiscoverGamesViews } from "./useDiscoverGamesViews";

export interface UseDiscoverGamesViewStateArgs {
	storageKey: string;
	discoverGamesColumns: ColumnConfig<GameRow>[];
	initialColumnVisibility: ColumnVisibility;
}

export function useDiscoverGamesViewState(args: UseDiscoverGamesViewStateArgs) {
	const views = useDiscoverGamesViews({ storageKey: args.storageKey });
	const grading = useDiscoverGamesGrading({
		storageKey: args.storageKey,
		customViewsKey: views.customViewsKey,
		selectedViewKey: views.selectedViewKey,
	});
	const layout = useDiscoverGamesColumnLayout({
		storageKey: args.storageKey,
		customViewsKey: views.customViewsKey,
		selectedViewKey: views.selectedViewKey,
		discoverGamesColumns: args.discoverGamesColumns,
		initialColumnVisibility: args.initialColumnVisibility,
	});

	const handleVisibilityChange = useCallback(
		(visibility: ColumnVisibility, order?: string[], colors?: GroupColors) => {
			layout.applyVisibility(visibility, order);
			if (colors !== undefined) {
				grading.setGroupColors(colors);
			}
		},
		[layout, grading],
	);

	const handleCustomViewsChange = useCallback(
		(nextViews: CustomView[], selectedId: string | null) => {
			views.setCustomViews(nextViews);
			views.setSelectedCustomViewId(selectedId);

			if (selectedId) {
				const selectedView = nextViews.find((v) => v.id === selectedId);
				if (selectedView) {
					layout.applyVisibility(selectedView.visibility);
					if (selectedView.groupColors) {
						grading.setGroupColors(selectedView.groupColors);
					}
				}
			}
		},
		[views, layout, grading],
	);

	return {
		...layout,
		groupColors: grading.groupColors,
		customViews: views.customViews,
		selectedCustomViewId: views.selectedCustomViewId,
		selectedViewName: views.selectedViewName,
		handleVisibilityChange,
		handleCustomViewsChange,
	};
}
